import {dolphinEventsBuilder, dolphinMetricsBuilder} from "../model/DolphinAnalyticEvents";
import AnalyticEventKeys from "./AnalyticEventKeys";
import {getDolphinAttributes} from "./ModuleAnalyticsUtility";
import AnalyticsClientId from "./AnalyticsClientId";
import {serializeAnalyticsEvent} from "./AnalyticsEventSerializer";
import {getKinesisClient} from "../client/KinesisClient";
import Logger from "../utils/Logger";
import {wrapWithTimeout} from "../utils/CommonUtils"
import Constants from "../constants/Constants";
import KinesisConstants from "../constants/KinesisConstants";
import { v1 as uuidv1 } from 'uuid';

// Firehose concatenates records and the Redshift COPY reads one JSON doc per line, so
// every record must end with a newline (matches the native producer; prod S3 objects
// end with 0x0a). Neither prod Firehose appends the delimiter, so this is load-bearing.
const RECORD_DELIMITER = "\n";

// Pinpoint mechanics, reproduced 1:1: events coalesce in an in-memory buffer and a
// timer flushes up to FLUSH_SIZE per putRecords call every FLUSH_INTERVAL_MS. The
// buffer is UNBOUNDED, matching Pinpoint (whose bufferSize:100 was miscoded and never
// enforced), and nothing is ever dropped at intake — dropping is loss Pinpoint never had.
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_SIZE = 100;
let buffer = [];
let flushTimer = null;

// Logger.log is null until setLogger() runs, and nothing guarantees that happens
// before the first event. Unguarded, a log on the failure path throws a TypeError and
// turns a handled failure into an unhandled exception inside a catch block.
const logWarn = (message) => { if (Logger.log) { Logger.log.warn(message); } };
const logError = (message) => { if (Logger.log) { Logger.log.error(message); } };

/**
 * ONE session per page load, minted lazily by the first event, start_timestamp frozen
 * once, never rotated. Mirrors Amplify's _generateSession default branch (uuid v1) —
 * the only branch the PWA reaches. Not sourced from SessionManager, whose
 * startNewSession fires on every MeshClient WebSocket reconnect and would fragment
 * PARTITION BY session_id mid-shift.
 */
let sessionId = null;
let sessionStartTimestamp = null;

const currentSession = () => {
    sessionId = sessionId || uuidv1();
    sessionStartTimestamp = sessionStartTimestamp || Date.now();
    return { id: sessionId, startTimestamp: sessionStartTimestamp };
};

/**
 * Resolves Kinesis config per region, defaulting to NA (matching the retired Pinpoint
 * resolver: IN, UK, JP and US routed to NA).
 */
function getKinesisConfig(region) {
    switch (region) {
        case Constants.Region.REGION_FE: return KinesisConstants.FE;
        case Constants.Region.REGION_EU: return KinesisConstants.EU;
        case Constants.Region.REGION_NA:
        default:                         return KinesisConstants.NA;
    }
}

export default {
    region: "",
    kinesis: null,
    streamName: null,
    context: null,

    async initializeAnalytics(region){
        this.region = region;
        const config = getKinesisConfig(region);

        this.kinesis = getKinesisClient(config.AWS_REGION, config.IDENTITY_POOL_ID);
        this.streamName = config.STREAM_NAME;

        // Warm Cognito credentials so the first write doesn't pay GetId +
        // GetCredentialsForIdentity inline. Guarded, fire-and-forget: never blocks init.
        const creds = this.kinesis.config.credentials;
        if (creds && typeof creds.getPromise === "function") {
            creds.getPromise().catch((e) => logWarn(`Cognito credential warm failed: ${e}`));
        }

        // Eager: localStorage is synchronous, so a lazy resolve would put I/O on a
        // scan-handling path.
        const clientId = AnalyticsClientId.get(config.LEGACY_APP_ID);

        this.context = {
            appId: config.LEGACY_APP_ID,
            cognitoIdentityPoolId: config.IDENTITY_POOL_ID,
            clientId: clientId
            // session is set by publish() on the first event, matching Amplify's
            // _generateSession (runs inside record(), not at configure() time).
        };

        // Start the flush timer once. unref() so it never keeps a test/node process
        // alive; harmless no-op in the browser where the PWA runs.
        if (!flushTimer) {
            flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
            if (flushTimer && typeof flushTimer.unref === "function") { flushTimer.unref(); }
        }
    },

    /**
     * Serializes one event. immediate events bypass the buffer and write a single
     * putRecord (so processAnalyticsWithTimeout's awaited timeout stays meaningful);
     * all others enqueue for the next flush. NEVER throws or rejects — consumers call
     * processAnalytics fire-and-forget with no .catch.
     *
     * Failures are logged, not counted: Pinpoint published no metrics on this path, and
     * this migration keeps that surface unchanged. Detection of a region-wide failure
     * (the missing unauth-role grant being the case that matters) is therefore a
     * pre-ship verification of the IAM grant, not a runtime signal.
     */
    publish(dolphinAnalytics) {
        const eventName = (dolphinAnalytics && dolphinAnalytics.name) || "UNKNOWN";

        if (!this.kinesis || !this.streamName) {
            logWarn("Analytics not initialized; dropping event " + eventName);
            return Promise.resolve();
        }

        const timestamp = Date.now();
        let json;
        try {
            const credentials = this.kinesis.config.credentials;
            this.context.cognitoId = (credentials && credentials.identityId) || "";
            this.context.session = currentSession();
            json = serializeAnalyticsEvent(dolphinAnalytics, this.context, timestamp);
        } catch (e) {
            logError(`Failed to serialize analytics event ${eventName}: ${e}`);
            return Promise.resolve();
        }

        const record = {
            Data: json + RECORD_DELIMITER,
            PartitionKey: this.context.clientId
        };

        // immediate bypass: single putRecord, awaited by the caller.
        if (dolphinAnalytics && dolphinAnalytics.immediate === true) {
            return this.publishImmediate(record);
        }

        // Buffered path: enqueue, flushed by the timer. Never refused at intake.
        buffer.push(record);
        return Promise.resolve();
    },

    /** Single-record write for the immediate path. Never throws or rejects. */
    publishImmediate(record) {
        try {
            return this.kinesis.putRecord({
                StreamName: this.streamName,
                Data: record.Data,
                PartitionKey: record.PartitionKey
            }).promise()
                .catch((error) => {
                    // Stringifying the error carries its code, so a denial is still
                    // identifiable in the log without branching on it.
                    logError(`Failed to publish analytics event: ${error}`);
                });
        } catch (e) {
            logError(`Failed to submit analytics event: ${e}`);
            return Promise.resolve();
        }
    },

    /**
     * Flushes up to FLUSH_SIZE buffered records in one putRecords call. putRecords is
     * partial-failure: a 200 can still carry FailedRecordCount>0, so we inspect each
     * entry's ErrorCode. Only throughput/internal errors are re-queued (once, front of
     * queue); a whole-request failure or timeout is DROPPED, not retried — Kinesis has
     * no de-duplication and a duplicate Redshift row is worse than a lost one. Never
     * throws or rejects.
     */
    flush() {
        if (!this.kinesis || !this.streamName || buffer.length === 0) {
            return Promise.resolve();
        }
        const batch = buffer.splice(0, FLUSH_SIZE);
        // Mapped rather than passed through: batch entries may carry _retried, which
        // aws-sdk param validation would reject as an unexpected member.
        const records = batch.map((r) => ({ Data: r.Data, PartitionKey: r.PartitionKey }));

        try {
            return this.kinesis.putRecords({
                StreamName: this.streamName,
                Records: records
            }).promise()
                .then((res) => {
                    if (res && res.FailedRecordCount > 0 && Array.isArray(res.Records)) {
                        const retry = [];
                        res.Records.forEach((rr, i) => {
                            const code = rr && rr.ErrorCode;
                            const rec = batch[i];
                            if (!code || !rec) { return; }
                            const retryable = code === "ProvisionedThroughputExceededException"
                                || code === "InternalFailure";
                            if (retryable && !rec._retried) {
                                rec._retried = true;      // one retry only
                                retry.push(rec);
                            } else {
                                logWarn(`Dropping analytics record after ${code}`);
                            }
                        });
                        if (retry.length) { buffer.unshift.apply(buffer, retry); }
                    }
                })
                .catch((error) => {
                    // Whole-request failure (incl. timeout): drop, do not re-queue.
                    logError(`Failed to flush analytics batch of ${batch.length}: ${error}`);
                });
        } catch (e) {
            logError(`Failed to submit analytics batch of ${batch.length}: ${e}`);
            return Promise.resolve();
        }
    },

    executeAPIAnalytics(module, startTime, failure) {

        const dolphinAttributes = getDolphinAttributes(this.region);
        dolphinAttributes.setModule(module)
            .setDescription(AnalyticEventKeys.Modules.PWA)
            .build();

        const dolphinMetrics = new dolphinMetricsBuilder()
            .setDuration(Date.now() - startTime)
        if (failure === true) {
            dolphinMetrics.setFailures()
                .build();
        }

        const dolphinAnalytics = new dolphinEventsBuilder()
            .setName(module)
            .setAttributes(dolphinAttributes)
            .setMetrics(dolphinMetrics)
            .build();
        // Deliberately not returned (pre-migration asymmetry); its rejection would be
        // unobservable anyway, one more reason publish() never rejects.
        this.publish(dolphinAnalytics);
    },
    processAnalytics(startTime, description, response, module, scanContent, description1,
                     eventName, failure, routeCode, immediate,
                     location, destination, currentStatus, reason, operatorLogin) {

        const dolphinAttributes = getDolphinAttributes(this.region);
        dolphinAttributes.setModule(module)
            .setDescription(description)
            .setScanContent(scanContent)
            .setDescription1(description1)

        if (routeCode) {
            dolphinAttributes.setRouteCode(routeCode)
        }
        if (location) {
            dolphinAttributes.setLocation(location)
        }
        if (destination) {
            dolphinAttributes.setDestination(destination)
        }
        if (currentStatus) {
            dolphinAttributes.setCurrentStatus(currentStatus)
        }
        if (reason) {
            dolphinAttributes.setReason(reason)
        }
        if (operatorLogin) {
            dolphinAttributes.setOperatorLogin(operatorLogin)
        }

        dolphinAttributes.build();

        const dolphinMetrics = new dolphinMetricsBuilder()
            .setDuration(Date.now() - startTime)

        if (failure === true) {
            dolphinMetrics.setFailures()
                .build();
        }

        const dolphinAnalytics = new dolphinEventsBuilder()
            .setName(eventName)
            .setAttributes(dolphinAttributes)
            .setMetrics(dolphinMetrics)
            .setImmediate(immediate)
            .build();

        // MUST return the promise: wrapWithTimeout is Promise.race, so returning
        // undefined resolves instantly and disables every timeout.
        return this.publish(dolphinAnalytics);
    },

    /**
     * Awaits the write up to publishTimeout ms (the underlying write is not cancelled
     * after timeout). Passes immediate: true so this event bypasses the buffer and is
     * a real single putRecord — otherwise the awaited timeout would race an enqueue
     * that resolves instantly and be meaningless.
     */
    async processAnalyticsWithTimeout(startTime, description, response, module, scanContent, description1,
                                      eventName, failure, routeCode, publishTimeout) {
        let publishPromise = this.processAnalytics(startTime, description, response, module, scanContent, description1,
            eventName, failure, routeCode, true);

        try {
            await wrapWithTimeout(publishPromise, publishTimeout);
        } catch (e) {
            logError(`Error while publishing event ${eventName}: ${e}`);
        }
    },

    /** Test seam: clears the buffer and stops the flush timer. */
    _resetForTest() {
        buffer = [];
        if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
    }
}
